import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Music, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  searchInstagramAudio,
  type ZernioAudioConfiguration,
  type ZernioAudioTrack,
} from '@/services/zernio.service';

/**
 * Pick a track from Instagram's licensed catalog for a Reel.
 *
 * WHY THIS IS NARROW. Zernio supports catalog audio on Instagram Reels and
 * nowhere else — "Stories, images and carousels reject audioConfiguration at
 * creation" — so the composer hides this entirely rather than offering it and
 * refusing the post afterwards.
 *
 * WHY IT IS PER ACCOUNT. The catalog is reached through the tenant that will
 * publish (`/accounts/{id}/instagram/audio`), so a track is searched through one
 * chosen Instagram account. With several connected, the first is used and named,
 * because the alternative — an account picker inside a music picker inside a
 * composer — buys precision nobody asked for.
 *
 * WHAT IT CANNOT KNOW. Whether this account may use audio at all. Zernio returns
 * no loginMethod, so an Instagram account connected without Facebook looks
 * identical to one that was, and the only way to find out is to ask. The error
 * that comes back is translated by the API and shown here rather than swallowed.
 */
export function ZernioAudioPicker({
  zernioAccountId,
  accountLabel,
  value,
  onChange,
  disabled,
}: {
  zernioAccountId: string;
  accountLabel: string;
  value: ZernioAudioConfiguration | null;
  onChange: (next: ZernioAudioConfiguration | null) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [chosen, setChosen] = useState<ZernioAudioTrack | null>(null);

  // The catalog call is a round trip per keystroke otherwise, and it is proxied
  // through our API to Zernio — slow enough that typing would queue requests.
  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), 350);
    return () => clearTimeout(id);
  }, [query]);

  const { data, isFetching, error } = useQuery({
    queryKey: ['zernio-audio', zernioAccountId, debounced],
    queryFn: () => searchInstagramAudio(zernioAccountId, debounced),
    // Only while the panel is open: a closed picker should cost nothing.
    enabled: open && zernioAccountId !== '',
    // An empty query is the trending list, which does not change minute to
    // minute; a search is worth keeping while the user reopens the panel.
    staleTime: 5 * 60_000,
    retry: false,
  });

  const tracks = data?.audio ?? [];

  const message = useMemo(() => {
    if (!error) return null;
    const detail = (error as { response?: { data?: { error?: { message?: string } } } })?.response
      ?.data?.error?.message;
    return detail ?? 'Music could not be loaded for this account.';
  }, [error]);

  const select = (track: ZernioAudioTrack) => {
    setChosen(track);
    // Full volume on the track and the original muted is what "add music to a
    // Reel" means to most people; both are adjustable below.
    onChange({ audioId: track.audioId, audioVolume: 100, videoVolume: 0 });
    setOpen(false);
  };

  const clear = () => {
    setChosen(null);
    onChange(null);
  };

  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Music className="h-4 w-4 shrink-0 text-slate-500" />
          {value && chosen ? (
            <span className="text-sm text-slate-700 truncate">
              <span className="font-medium">{chosen.title}</span>
              {chosen.artist && <span className="text-slate-500"> · {chosen.artist}</span>}
            </span>
          ) : (
            <span className="text-sm text-slate-600">Add music from Instagram</span>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {value && (
            <button
              type="button"
              onClick={clear}
              disabled={disabled}
              className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
            >
              <X className="h-3 w-3" /> Remove
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            disabled={disabled}
            className="text-xs px-2.5 py-1 rounded border border-slate-200 hover:bg-slate-50"
          >
            {open ? 'Close' : value ? 'Change' : 'Browse'}
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-3 space-y-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search music, or leave blank for trending"
              className="w-full h-8 pl-7 pr-2 text-xs rounded border border-slate-200 focus:outline-none focus:ring-1 focus:ring-blue-300"
            />
          </div>

          <p className="text-[11px] text-slate-500">Searching the catalog for {accountLabel}.</p>

          {message ? (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              {message}
            </p>
          ) : (
            <div className="max-h-48 overflow-y-auto rounded border border-slate-200 divide-y divide-slate-100">
              {isFetching && tracks.length === 0 ? (
                <p className="px-2.5 py-3 text-xs text-slate-500">Loading…</p>
              ) : tracks.length === 0 ? (
                <p className="px-2.5 py-3 text-xs text-slate-500">No tracks found.</p>
              ) : (
                tracks.map((track) => (
                  <button
                    key={track.audioId}
                    type="button"
                    onClick={() => select(track)}
                    className="w-full text-left px-2.5 py-2 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
                  >
                    <span className="block text-xs font-medium text-slate-900 truncate">
                      {track.title || 'Untitled'}
                    </span>
                    <span className="block text-[11px] text-slate-500 truncate">
                      {track.artist}
                      {track.durationInMs > 0 && ` · ${Math.round(track.durationInMs / 1000)}s`}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {value && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(
            [
              ['Music', 'audioVolume'],
              ['Original audio', 'videoVolume'],
            ] as const
          ).map(([label, key]) => (
            <label key={key} className="text-[11px] text-slate-600">
              {label}: {value[key]}%
              <input
                type="range"
                min={0}
                max={100}
                value={value[key]}
                disabled={disabled}
                onChange={(e) => onChange({ ...value, [key]: Number(e.target.value) })}
                className={cn('w-full mt-1', disabled && 'opacity-60')}
              />
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
